<!--
  数据统计页面。
  用于查看系统使用量、反馈质量、Token 消耗和最近会话等运营概览。
-->
<template>
  <div class="stats-page">
    <div class="header">
      <h1>数据统计</h1>
      <p>系统使用、反馈质量与 Token 成本概览</p>
    </div>

    <div v-if="loading" class="loading">
      <el-skeleton :rows="6" animated />
    </div>

    <div v-else-if="error" class="error">
      <el-alert title="加载失败" type="error" :description="error" :closable="false" />
    </div>

    <div v-else class="stats-content">
      <div class="overview-cards">
        <el-card v-for="card in overviewCards" :key="card.label" class="overview-card">
          <div class="card-content">
            <div class="card-icon">
              <el-icon>
                <component :is="card.icon" />
              </el-icon>
            </div>
            <div class="card-info">
              <div class="card-value">{{ card.value }}</div>
              <div class="card-label">
                <span>{{ card.label }}</span>
                <el-tooltip :content="card.description" placement="top" effect="dark">
                  <el-icon class="label-help-icon" :aria-label="`${card.label}说明`">
                    <QuestionFilled />
                  </el-icon>
                </el-tooltip>
              </div>
            </div>
          </div>
        </el-card>
      </div>

      <div class="charts-grid">
        <el-card class="chart-card">
          <template #header>
            <div class="card-header"><span>最近 30 天常见提问 Top 10</span></div>
          </template>
          <div v-if="stats.hotQuestions.length > 0" class="chart-container">
            <v-chart :option="hotQuestionsOption" :style="{ height: '400px' }" />
          </div>
          <el-empty v-else description="暂无数据" />
        </el-card>

        <el-card class="chart-card">
          <template #header>
            <div class="card-header"><span>反馈统计</span></div>
          </template>
          <div v-if="stats.feedbackStats.assistantMessageCount > 0" class="chart-container">
            <v-chart :option="feedbackOption" :style="{ height: '400px' }" />
          </div>
          <el-empty v-else description="暂无数据" />
        </el-card>
      </div>

      <div class="charts-grid">
        <el-card class="chart-card">
          <template #header>
            <div class="card-header"><span>最近 30 天 Token 趋势</span></div>
          </template>
          <div v-if="stats.tokenUsage.dailyTrend.length > 0" class="chart-container">
            <v-chart :option="tokenTrendOption" :style="{ height: '320px' }" />
          </div>
          <el-empty v-else description="暂无 Token 数据" />
        </el-card>

        <el-card class="chart-card">
          <template #header>
            <div class="card-header"><span>模型 Token 消耗</span></div>
          </template>
          <el-table :data="stats.tokenUsage.modelBreakdown" border>
            <el-table-column prop="model" label="模型" min-width="160" />
            <el-table-column label="请求数" width="100">
              <template #default="{ row }">{{ formatNumber(row.requestCount) }}</template>
            </el-table-column>
            <el-table-column label="Token" min-width="120">
              <template #default="{ row }">{{ formatNumber(row.totalTokens) }}</template>
            </el-table-column>
            <el-table-column label="平均 Token" width="120">
              <template #default="{ row }">{{ formatNumber(row.avgTokens) }}</template>
            </el-table-column>
            <el-table-column label="降级次数" width="100">
              <template #default="{ row }">{{ formatNumber(row.fallbackCount) }}</template>
            </el-table-column>
            <el-table-column label="估算成本" width="120">
              <template #default="{ row }">${{ row.estimatedCostUsd.toFixed(4) }}</template>
            </el-table-column>
          </el-table>
        </el-card>
      </div>

      <el-card class="chart-card">
        <template #header>
          <div class="card-header"><span>Prompt 版本 Token 消耗</span></div>
        </template>
        <el-table :data="stats.tokenUsage.promptVersionBreakdown" border>
          <el-table-column prop="promptVersion" label="Prompt 版本" min-width="160" />
          <el-table-column label="请求数" width="120">
            <template #default="{ row }">{{ formatNumber(row.requestCount) }}</template>
          </el-table-column>
          <el-table-column label="Token" min-width="140">
            <template #default="{ row }">{{ formatNumber(row.totalTokens) }}</template>
          </el-table-column>
          <el-table-column label="平均 Token" width="130">
            <template #default="{ row }">{{ formatNumber(row.avgTokens) }}</template>
          </el-table-column>
          <el-table-column label="降级次数" width="120">
            <template #default="{ row }">{{ formatNumber(row.fallbackCount) }}</template>
          </el-table-column>
          <el-table-column label="估算成本" width="140">
            <template #default="{ row }">${{ row.estimatedCostUsd.toFixed(4) }}</template>
          </el-table-column>
        </el-table>
      </el-card>

      <el-card v-if="stats.tokenUsage.message" class="info-card">
        <template #header>
          <div class="card-header"><span>可统计 Token 小结</span></div>
        </template>
        <div class="info-content token-summary">
          <el-alert type="info" :closable="false" show-icon>
            {{ stats.tokenUsage.message }}
          </el-alert>
          <div v-if="stats.tokenUsage.latestRecord" class="latest-usage">
            <div class="latest-title">最近一次记录</div>
            <div class="latest-grid">
              <div>
                <span>模型</span>
                <strong>{{ stats.tokenUsage.latestRecord.model }}</strong>
              </div>
              <div>
                <span>Provider</span>
                <strong>{{ stats.tokenUsage.latestRecord.provider }}</strong>
              </div>
              <div>
                <span>Prompt 版本</span>
                <strong>{{ stats.tokenUsage.latestRecord.promptVersion }}</strong>
              </div>
              <div>
                <span>输入 Token</span>
                <strong>{{ formatNumber(stats.tokenUsage.latestRecord.promptTokens) }}</strong>
              </div>
              <div>
                <span>输出 Token</span>
                <strong>{{ formatNumber(stats.tokenUsage.latestRecord.completionTokens) }}</strong>
              </div>
              <div>
                <span>总 Token</span>
                <strong>{{ formatNumber(stats.tokenUsage.latestRecord.totalTokens) }}</strong>
              </div>
              <div>
                <span>估算成本</span>
                <strong>${{ stats.tokenUsage.latestRecord.estimatedCostUsd.toFixed(4) }}</strong>
              </div>
              <div>
                <span>记录时间</span>
                <strong>{{ formatDateTime(stats.tokenUsage.latestRecord.createdAt) }}</strong>
              </div>
            </div>
          </div>
        </div>
      </el-card>

      <el-card class="chart-card">
        <template #header>
          <div class="card-header"><span>自动降级记录</span></div>
        </template>
        <el-table v-if="stats.tokenUsage.fallbackLogs.length > 0" :data="stats.tokenUsage.fallbackLogs" border>
          <el-table-column label="时间" width="170">
            <template #default="{ row }">{{ formatDateTime(row.createdAt) }}</template>
          </el-table-column>
          <el-table-column prop="fromModel" label="来源" min-width="120" />
          <el-table-column prop="toModel" label="降级到" min-width="180" />
          <el-table-column prop="promptVersion" label="Prompt" width="120" />
          <el-table-column label="Token" width="120">
            <template #default="{ row }">{{ formatNumber(row.totalTokens) }}</template>
          </el-table-column>
          <el-table-column label="成本" width="120">
            <template #default="{ row }">${{ row.estimatedCostUsd.toFixed(4) }}</template>
          </el-table-column>
          <el-table-column prop="traceId" label="Trace ID" min-width="220" />
        </el-table>
        <el-empty v-else description="暂无自动降级记录" />
      </el-card>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart, LineChart, PieChart } from "echarts/charts";
import { GridComponent, LegendComponent, TitleComponent, TooltipComponent } from "echarts/components";
import VChart from "vue-echarts";
import { ElMessage } from "element-plus/es/components/message";
import { ChatDotRound, Coin, DataLine, Message, Pointer, QuestionFilled, Tickets } from "@element-plus/icons-vue";

// 引入 ECharts 主模块
use([CanvasRenderer, BarChart, LineChart, PieChart, TitleComponent, TooltipComponent, GridComponent, LegendComponent]);

interface HotQuestion {
  question: string;
  normalizedQuestion: string;
  count: number;
}

interface FeedbackStats {
  likeCount: number;
  dislikeCount: number;
  unfeedbackCount: number;
  likeRate: number;
  dislikeRate: number;
  totalFeedbackCount: number;
  assistantMessageCount: number;
  feedbackCoverageRate: number;
  feedbackCoverageText: string;
}

interface Overview {
  totalMessages: number;
  totalSessions: number;
}

interface UsageBreakdown {
  model?: string;
  promptVersion?: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  requestCount: number;
  avgTokens: number;
  avgCostUsd: number;
  estimatedCostUsd: number;
  fallbackCount: number;
}

interface DailyUsage {
  day: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  requestCount: number;
  estimatedCostUsd: number;
}

interface LatestUsageRecord {
  model: string;
  provider: string;
  promptVersion: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  estimatedCostUsd: number;
  createdAt: string | number | Date;
}

interface TokenUsage {
  total: number;
  promptTokens: number;
  completionTokens: number;
  requestCount: number;
  estimatedCostUsd: number;
  modelBreakdown: UsageBreakdown[];
  promptVersionBreakdown: UsageBreakdown[];
  dailyTrend: DailyUsage[];
  latestRecord: LatestUsageRecord | null;
  fallbackLogs: FallbackLog[];
  message: string;
}

interface FallbackLog {
  traceId: string;
  sessionId: string;
  fromModel: string;
  toModel: string;
  provider: string;
  promptVersion: string;
  totalTokens: number;
  estimatedCostUsd: number;
  createdAt: string | number | Date;
}

interface StatsData {
  hotQuestions: HotQuestion[];
  feedbackStats: FeedbackStats;
  tokenUsage: TokenUsage;
  overview: Overview;
}

/**
 * 创建一个空的 TokenUsage 对象，用于初始化 stats.tokenUsage 属性。
 */
const emptyTokenUsage = (): TokenUsage => ({
  total: 0, // 总 Token 数，用于图表展示的总和
  promptTokens: 0, // 提示词 Token 数
  completionTokens: 0, // 完成词 Token 数
  requestCount: 0, // 请求数
  estimatedCostUsd: 0, // 估算成本（美元）
  modelBreakdown: [], // 模型 Token 分段统计
  promptVersionBreakdown: [], // Prompt 版本 Token 分段统计
  dailyTrend: [], // 每日 Token 使用趋势
  latestRecord: null, // 最新一次 Token 记录
  fallbackLogs: [], // 自动降级记录
  message: "", // 用量说明
});

const loading = ref(true);
const error = ref("");
// 数据
const stats = ref<StatsData>({
  hotQuestions: [], // 热门问题
  feedbackStats: { // 反馈统计
    likeCount: 0, // 点赞数
    dislikeCount: 0, // 点踩数
    unfeedbackCount: 0, // 未反馈的 AI 回复数
    likeRate: 0, // 有反馈样本中的点赞占比
    dislikeRate: 0, // 有反馈样本中的点踩占比
    totalFeedbackCount: 0, // 反馈总数
    assistantMessageCount: 0, // AI 回复总数
    feedbackCoverageRate: 0, // 反馈覆盖率
    feedbackCoverageText: "0/0", // 反馈数 / AI 回复数
  },
  tokenUsage: emptyTokenUsage(), // Token 用量 
  overview: {
    totalMessages: 0, // 总消息数
    totalSessions: 0, // 总会话数
  },
});
// 数字格式化
const formatNumber = (value: number) => new Intl.NumberFormat("zh-CN").format(value || 0);
const formatDateTime = (value: string | number | Date) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

// 概览卡片
const overviewCards = computed(() => [
  {
    label: "总会话数",
    value: formatNumber(stats.value.overview.totalSessions),
    icon: ChatDotRound,
    description: "已经产生过消息的对话数量，用来观察实际发生了多少轮独立聊天。",
  },
  {
    label: "总消息数",
    value: formatNumber(stats.value.overview.totalMessages),
    icon: Message,
    description: "当前系统累计产生的对话消息总数，包含用户提问和 AI 回复。",
  },
  {
    label: "反馈数/回复数",
    value: stats.value.feedbackStats.feedbackCoverageText,
    icon: Pointer,
    description: "有多少条 AI 回复收到了点赞或点踩，例如 2/100 表示 100 条回复中有 2 条被反馈。",
  },
  {
    label: "模型请求",
    value: formatNumber(stats.value.tokenUsage.requestCount),
    icon: Tickets,
    description: "系统成功记录到用量的 AI 调用次数，用来观察模型服务被使用了多少次。",
  },
  {
    label: "Token 用量",
    value: formatNumber(stats.value.tokenUsage.total),
    icon: DataLine,
    description: "成功记录到用量的 AI 调用所消耗的总 Token，可理解为模型实际处理的文字量。",
  },
  {
    label: "估算成本",
    value: `$${stats.value.tokenUsage.estimatedCostUsd.toFixed(4)}`,
    icon: Coin,
    description: "根据已记录的 Token 用量和项目配置单价估出的参考成本，不等同于服务商最终账单。它只统计系统成功记录到 Token 用量的 AI 调用。",
  },
]);

// 热门问题图表
const hotQuestionsOption = computed(() => ({
  tooltip: {
    trigger: "axis",
    axisPointer: { type: "shadow" },
    formatter: (params: any) => {
      const item = Array.isArray(params) ? params[0] : params;
      const question = stats.value.hotQuestions[item?.dataIndex];
      return question
        ? `${question.question}<br/>归并后的问题：${question.normalizedQuestion}<br/>最近 30 天提问次数：${question.count}`
        : "";
    },
  },
  grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true },
  xAxis: { type: "value", boundaryGap: [0, 0.01], name: "提问次数" },
  yAxis: { type: "category", data: stats.value.hotQuestions.map((item) => item.question) },
  series: [{ name: "提问次数", type: "bar", data: stats.value.hotQuestions.map((item) => item.count), itemStyle: { color: "#409eff" } }],
}));

// 反馈图表
const feedbackOption = computed(() => ({
  tooltip: { trigger: "item", formatter: "{a} <br/>{b}: {c} ({d}%)" },
  legend: { orient: "vertical", left: "left", data: ["点赞", "点踩", "未反馈"] },
  series: [{
    name: "AI 回复反馈",
    type: "pie",
    radius: "50%",
    data: [
      { value: stats.value.feedbackStats.likeCount, name: "点赞", itemStyle: { color: "#67c23a" } },
      { value: stats.value.feedbackStats.dislikeCount, name: "点踩", itemStyle: { color: "#f56c6c" } },
      { value: stats.value.feedbackStats.unfeedbackCount, name: "未反馈", itemStyle: { color: "#c0c4cc" } },
    ],
  }],
}));

// Token 用量趋势图表
const tokenTrendOption = computed(() => ({
  tooltip: {
    trigger: "axis",
    formatter: (params: any) => {
      const item = Array.isArray(params) ? params[0] : params;
      const row = stats.value.tokenUsage.dailyTrend[item?.dataIndex];
      return row
        ? `${row.day}<br/>Token：${formatNumber(row.totalTokens)}<br/>请求数：${formatNumber(row.requestCount)}<br/>估算成本：$${row.estimatedCostUsd.toFixed(4)}`
        : "";
    },
  },
  grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true },
  xAxis: { type: "category", data: stats.value.tokenUsage.dailyTrend.map((item) => item.day) },
  yAxis: { type: "value", name: "Token" },
  series: [{ name: "Token", type: "line", smooth: true, data: stats.value.tokenUsage.dailyTrend.map((item) => item.totalTokens), itemStyle: { color: "#67c23a" } }],
}));

// Token 用量模型分布图表
const fetchStats = async () => {
  try {
    loading.value = true;
    error.value = "";
    const response = await $fetch<{ success: boolean; data: StatsData }>("/api/stats/stats");
    if (!response.success) {
      throw new Error("获取统计数据失败");
    }
    stats.value = {
      ...response.data,
      tokenUsage: {
        ...emptyTokenUsage(),
        ...response.data.tokenUsage,
      },
    };
  } catch (e: any) {
    console.error("获取统计数据失败:", e);
    error.value = e.message || "获取统计数据失败";
    ElMessage.error("获取统计数据失败");
  } finally {
    loading.value = false;
  }
};

onMounted(fetchStats);
</script>

<style scoped>
.stats-page {
  height: calc(100vh - 64px);
  padding: 24px;
  margin: 0 auto;
  overflow-y: auto;
}

.header {
  margin-bottom: 24px;
}

.header h1 {
  margin: 0 0 6px;
  color: #303133;
  font-size: 24px;
}

.header p {
  margin: 0;
  color: #606266;
  font-size: 14px;
}

.loading,
.error {
  margin-top: 24px;
}

.stats-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.overview-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.overview-card,
.chart-card,
.info-card {
  border: none;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.card-content {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 0;
}

.card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  color: white;
  font-size: 24px;
  background: #409eff;
  border-radius: 8px;
}

.card-info {
  flex: 1;
  min-width: 0;
}

.card-value {
  margin-bottom: 4px;
  color: #303133;
  font-size: 26px;
  font-weight: 600;
  line-height: 1.1;
  word-break: break-word;
}

.card-label {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #909399;
  font-size: 13px;
}

.label-help-icon {
  flex: 0 0 auto;
  color: #c0c4cc;
  font-size: 14px;
  cursor: help;
  transition: color 0.2s ease;
}

.label-help-icon:hover {
  color: #409eff;
}

.charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 16px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  /* padding: 0 10px; */
  color: #303133;
  font-weight: 600;
}

.chart-container {
  box-sizing: border-box;
  width: 100%;
  padding: 10px 0 10px 10px;
}

.info-content {
  padding: 12px 0;
}

.token-summary {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.latest-usage {
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
}

.latest-title {
  margin-bottom: 12px;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.latest-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
}

.latest-grid div {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.latest-grid span {
  color: #909399;
  font-size: 12px;
}

.latest-grid strong {
  overflow-wrap: anywhere;
  color: #303133;
  font-size: 14px;
}

@media (max-width: 768px) {
  .stats-page {
    padding: 16px;
  }

  .overview-cards,
  .charts-grid {
    grid-template-columns: 1fr;
  }

  .header h1 {
    font-size: 24px;
  }
}
</style>
